import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryOptions } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { makeIsEnabled } from '@cast/utils';
import { ListEC2NodeClassesResponse } from '@cast/types-kent';

const KENT_EC2_NODE_CLASSES_QUERY_KEY = 'kent-ec2-node-classes';

export const getEC2NodeClassesQueryKey = (clusterId?: string) =>
  [KENT_EC2_NODE_CLASSES_QUERY_KEY, clusterId] as const;

/** Reusable query options for the EC2 node-classes list (also subscribed by the edit dialog's 409 reload). */
export const getEC2NodeClassesQuery = ({
  organizationId,
  clusterId,
}: {
  organizationId: string;
  clusterId: string;
}) =>
  queryOptions({
    queryKey: getEC2NodeClassesQueryKey(clusterId),
    queryFn: async () => {
      const { data } = await apiClient.kent.getEc2NodeClasses({
        organizationId,
        clusterId,
      });
      return data;
    },
  });

type Params = {
  clusterId: string;
};

type GetEC2NodeClassesQueryArgs = {
  params: Params;
  options?: QueryOptions<ListEC2NodeClassesResponse>;
};

export const useGetEC2NodeClassesQuery = ({
  params,
  options,
}: GetEC2NodeClassesQueryArgs) => {
  const { currentOrganization } = useOrganizations();
  const organizationId = currentOrganization?.id;

  return useQuery({
    queryKey: getEC2NodeClassesQueryKey(params.clusterId),
    queryFn: async () => {
      const { data } = await apiClient.kent.getEc2NodeClasses({
        organizationId: organizationId!,
        clusterId: params.clusterId,
      });
      return data;
    },
    ...options,
    enabled: makeIsEnabled(
      !!organizationId && !!params.clusterId,
      options?.enabled
    ),
  });
};
